/**
 * When a schedule fires, in the shape the API stores it.
 *
 * `cronExpression`, `intervalSeconds` and `runAtDateTime` are top-level fields
 * on the schedule — the scheduler reads them directly. Exactly one applies, and
 * the others must be absent: a CRON schedule carrying a stale `intervalSeconds`
 * is a schedule with two answers to one question.
 *
 * Mirrors the Background Scheduler's helpers deliberately, so the two screens
 * describe the same thing the same way rather than drifting apart.
 */

export type EngineScheduleType = 'CRON' | 'INTERVAL' | 'ONCE' | 'EVENT';

export interface Timing {
  cronExpression?: string | null;
  intervalSeconds?: number | null;
  runAtDateTime?: string | null;
  eventKey?: string | null;
}

/** The engine's finest resolution — the poller cannot see anything shorter. */
export const POLL_INTERVAL_SECONDS = 5;

/** A fresh timing for a type. Every other field is cleared, never carried over. */
export const blankTiming = (t: string): Timing => ({
  cronExpression: t === 'CRON' ? '0 9 * * *' : null,
  intervalSeconds: t === 'INTERVAL' ? 300 : null,
  runAtDateTime: null,
  eventKey: null,
});

/** One line saying when this fires, for a list column. */
export const describeTiming = (s: { scheduleType?: string } & Timing): string => {
  if (s.scheduleType === 'CRON') return s.cronExpression || '—';
  if (s.scheduleType === 'EVENT') return s.eventKey ? `on ${s.eventKey}` : '—';
  if (s.scheduleType === 'INTERVAL') {
    const n = s.intervalSeconds || 0;
    if (!n) return '—';
    if (n % 3600 === 0) return `every ${n / 3600}h`;
    if (n % 60 === 0) return `every ${n / 60}m`;
    return `every ${n}s`;
  }
  return s.runAtDateTime ? String(s.runAtDateTime).replace('T', ' ').slice(0, 16) : '—';
};

/**
 * Datetimes are naive IST server-side. Sending a UTC string for a locally
 * picked time shifts it by five and a half hours, so it is serialised without
 * a zone — the same rule the Background Scheduler follows.
 */
export const toNaiveIso = (d: Date | null | undefined): string | null => {
  if (!d) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}:00`;
};

export const fromNaiveIso = (s?: string | null): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};
